"use client";

import { Suspense, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import DashboardHeader from "@/components/headers/DashboardHeader";
import FooterLight from "@/components/FooterLight";
import PaymentResultModal from "@/components/PaymentResultModal";
import DashboardSkeleton from "@/components/DashboardSkeleton";
import { useCreditsContext } from "@/contexts/CreditsContext";
import { trackPurchase, getPurchaseContext } from "@/libs/ga";
import { trackFunnel, getLastToolId } from "@/libs/analytics";
import {
  DASHBOARD_FREE_TRAY_V1_EXPERIMENT_ID,
  getDevExperimentOverride,
  resolveDashboardFreeTrayVariant,
} from "@/libs/experiments";
import { isLandingCreatePfp, parseLandingDemoId, parseLandingPpUid } from "@/libs/landingHandoff";
import JtbdDashboardV2 from "@/components/dashboard/jtbd/JtbdDashboardV2";

export const dynamic = "force-dynamic";

function DashboardV2Content() {
  const searchParams = useSearchParams();
  const { refreshCredits, isLoading: creditsLoading } = useCreditsContext();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTrayByExperiment, setShowTrayByExperiment] = useState(true);

  const fromLanding = isLandingCreatePfp({
    from: searchParams.get("from"),
    intent: searchParams.get("intent"),
  });
  const landingDemoId = parseLandingDemoId(searchParams.get("demo"));
  const landingPpUid = landingDemoId
    ? null
    : parseLandingPpUid({
        ppUid: searchParams.get("pp_uid"),
        pollUrl: searchParams.get("poll_url"),
      });
  const landingHandoffSource = landingDemoId ? "demo" : landingPpUid ? "upload" : fromLanding ? "fallback" : null;
  const showFreePfpTray = showTrayByExperiment || Boolean(landingDemoId) || Boolean(landingPpUid);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const applyOverride = () => {
      setShowTrayByExperiment(
        resolveDashboardFreeTrayVariant(getDevExperimentOverride(DASHBOARD_FREE_TRAY_V1_EXPERIMENT_ID)) === "tray"
      );
    };
    applyOverride();
    const onOverride = () => applyOverride();
    window.addEventListener("storage", onOverride);
    window.addEventListener("exp-override", onOverride);
    return () => {
      window.removeEventListener("storage", onOverride);
      window.removeEventListener("exp-override", onOverride);
    };
  }, []);

  useEffect(() => {
    const paymentSuccess = searchParams.get("payment_success");
    if (paymentSuccess !== "true" && paymentSuccess !== "false") return;

    setShowPaymentModal(true);
    if (paymentSuccess === "true") {
      const purchaseContext = getPurchaseContext(true);
      if (purchaseContext && Object.keys(purchaseContext).length > 0) {
        trackFunnel("monetization", "purchase_completed", {
          ...purchaseContext,
          last_tool_id: getLastToolId() || undefined,
          return_url: window.location.href,
          success: true,
        });
        const sessionId = searchParams.get("session_id");
        trackPurchase({
          transaction_id: sessionId || `pfp_${Date.now()}`,
          value: purchaseContext.value || 0,
          currency: "USD",
          purchase_trigger: purchaseContext.purchase_trigger,
          package_type: purchaseContext.package_type,
          credits: purchaseContext.credits,
          items: [
            {
              item_id: purchaseContext.price_id || sessionId || "unknown",
              item_name: purchaseContext.credits
                ? `${purchaseContext.credits} Credits`
                : purchaseContext.package_type || "Purchase",
              item_category: purchaseContext.package_type || "credits",
              quantity: 1,
              price: purchaseContext.value || 0,
            },
          ],
        });
      }
      refreshCredits();
    }
  }, [searchParams, refreshCredits]);

  if (creditsLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      {showPaymentModal && <PaymentResultModal onClose={() => setShowPaymentModal(false)} />}

      <div className="fixed top-0 right-0 w-full h-full pointer-events-none z-[-1] overflow-hidden">
        <Image
          src="/assets/images/shaped_bg.svg"
          alt=""
          width={800}
          height={800}
          className="absolute top-0 right-0 w-[50%] max-w-[800px]"
          priority={false}
        />
      </div>

      <main className="flex-1 pb-24">
        <DashboardHeader />
        <section className="pt-2 md:pt-8">
          <JtbdDashboardV2
            showFreePfpTray={showFreePfpTray}
            landingDemoId={landingDemoId}
            landingPpUid={landingPpUid}
            landingHandoffSource={landingHandoffSource}
          />
        </section>
      </main>

      <FooterLight />
    </div>
  );
}

export default function DashboardV2() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <DashboardV2Content />
    </Suspense>
  );
}
